import React, { useEffect, useState } from 'react';
import {
  CreditLineConfirmationDetails,
  CreditLineConfirmationCosts,
} from '@/features/application/components';
import styles from './CreditLineConfirmationPage.module.scss';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { useLocation, useNavigate } from 'react-router-dom';
import { OceanOfferHero } from '@/features/offer/components/OceanOfferHero';
import {
  FinalOfferChangesBanner,
  type OfferChangesState,
} from '../../components/FinalOfferChangesBanner';
import { routes } from '@/routes/routes';
import { useApplication } from '../../hooks';
import { useLoanOffer } from '@/features/offer';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { useLoan } from '@/features/loan';
import { calculateCreditLineOfferDetails } from '@/features/offer/hooks/loanOfferUtils';
import { useKyc } from '@/features/kyc/hooks/useKyc';
import { trackingService } from '@/lib/tracking';

export interface CreditLineConfirmationPageProps {
  className?: string;
}

export const CreditLineConfirmationPage: React.FC<
  CreditLineConfirmationPageProps
> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from the chosen preliminary credit, which ANAF changed.
  const [offerChanges, setOfferChanges] = useState(
    () => (location.state as OfferChangesState | null)?.offerChanges
  );
  const { creditLineDraft, applyOffer, isApplyingOffer, getApplicationState } =
    useApplication();
  const {
    creditLineOffer,
    loanOfferState,
    isLoading: isLoanOfferLoading,
  } = useLoanOffer(true);
  const { requestContractSignCode } = useLoan();
  const { requiresProofOfAddressVerification } = useKyc(true);

  // event_id is the scoring id, so repeat visits on the same offers dedupe.
  useEffect(() => {
    if (isLoanOfferLoading) {
      return;
    }
    void trackingService.trackEvent('final_offer_summary_shown', {
      type: 'credit-line',
      event_id: loanOfferState?.id ?? undefined,
    });
  }, [isLoanOfferLoading]);

  const details = calculateCreditLineOfferDetails(
    creditLineOffer,
    creditLineDraft
  );


  const isDisabled = !details || isApplyingOffer;

  const handleConfirm = async () => {
    if (!creditLineOffer || !details) {
      throw new Error('Credit line offer not found');
    }

    if (requiresProofOfAddressVerification) {
      navigate(routes.kyc.proofOfAddress);
      return;
    }

    const contract = await applyOffer(
      creditLineOffer.id,
      creditLineOffer.type,
      creditLineOffer.creditProductName,
      details.selectedAmount,
      undefined,
      details.firstBillDate,
      details.isDerogation,
      // Declared on the signing screen, without which the contract is not
      // signed.
      true
    );

    if (!contract) {
      throw new Error('Failed to apply offer');
    }

    await getApplicationState();
    await requestContractSignCode(contract.id);
    navigate(routes.loan.sign);
  };

  const handleEditCreditLimit = () => {
    navigate(routes.application.creditLineAmount);
  };

  const handleEditPaymentDay = () => {
    navigate(routes.application.creditLinePaymentDate);
  };

  if (isLoanOfferLoading) {
    return <PageLoader />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Main Content */}
        <div className={styles.mainContent}>
          {creditLineOffer && (
            <OceanOfferHero
              type="credit-line"
              amount={details?.selectedAmount ?? creditLineOffer.creditLimit}
              numberOfRates={creditLineOffer.numberOfRates}
            />
          )}
          {offerChanges && (
            <FinalOfferChangesBanner
              changes={offerChanges}
              onClose={() => setOfferChanges(undefined)}
            />
          )}
          {/* Credit Line Details Section */}
          <CreditLineConfirmationDetails
            creditLimit={details?.selectedAmount}
            paymentDay={details?.firstBillDate?.getDate()}
            finalDueDate={details?.finalDueDate}
            showEditIcons={true}
            onEditCreditLimit={handleEditCreditLimit}
            onEditPaymentDay={handleEditPaymentDay}
          />

          {/* Costs Section */}
          <CreditLineConfirmationCosts
            contractDuration={creditLineOffer?.numberOfRates}
            dailyInterestRate={creditLineOffer?.interestDailyPercentage}
            nominalAnnualPercentageRate={
              creditLineOffer?.nominalAnnualPercentageRate
            }
            effectiveAnnualPercentageRate={
              creditLineOffer?.effectiveAnnualPercentageRate
            }
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          trackingId="confirm_credit_line"
          className={styles.button}
          text="Continuă spre contract"
          rightIcon={<Icon name="arrow-right" />}
          onClick={handleConfirm}
          loading={isApplyingOffer}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};
